import React, { useEffect, useMemo, useState } from 'react';
import { Paper, Typography, Button, TextField, Snackbar, Alert } from '@mui/material';
import { useProtectedRoute } from '../../../hooks/useProtectedRoute';
import { useAuth } from '../../../context/AuthContext';
import { listSubscriptionBills, sendSubscriptionBill, getSubscriptionInvoicePreview } from '../../../services/admin';
import { Link } from 'react-router-dom';

export default function SubscriptionBillingPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const can = role === 'SUPERADMIN';
  const [bills, setBills] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success'|'error'|'info'>('success');

  useEffect(() => { (async () => {
    if (!token || !can) return; setLoading(true); setError(null);
    try { const data = await listSubscriptionBills(token); setBills(data); } catch (e:any){ setError(e?.response?.data?.message || 'Failed'); } finally { setLoading(false); }
  })(); }, [token, can]);

  const filtered = useMemo(() => {
    if (!filter) return bills;
    const f = filter.toLowerCase();
    return bills.filter((b:any) => String(b.invoiceNumber).toLowerCase().includes(f) || String(b.tenantId).toLowerCase().includes(f) || String(b.status).toLowerCase().includes(f));
  }, [bills, filter]);

  const preview = async (billId: string, autoPrint?: boolean) => {
    if (!token) return;
    try {
      const html = await getSubscriptionInvoicePreview(token, billId, { print: !!autoPrint });
      const w = window.open('', '_blank');
      if (!w) { alert('Please allow popups to preview'); return; }
      w.document.open(); w.document.write(html); w.document.close();
    } catch (e:any) { setError(e?.response?.data?.message || 'Failed to load preview'); }
  };

  // removed unused 'resend' to satisfy lint

  const sendBill = async (billId: string, channel: 'email'|'whatsapp') => {
    if (!token) return; setLoading(true); setError(null);
    try { await sendSubscriptionBill(token, billId, { channel }); setToastSeverity('success'); setToastMsg(`${channel === 'email' ? 'Email' : 'WhatsApp'} sent`); setToastOpen(true); }
    catch (e:any){ const msg = e?.response?.data?.message || `Failed to send via ${channel}`; setError(msg); setToastSeverity('error'); setToastMsg(msg); setToastOpen(true); }
    finally { setLoading(false); }
  };

  if (!can) return <Paper elevation={0} className="p-6 rounded-2xl"><Typography>You do not have access.</Typography></Paper>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="h5" fontWeight={900}>Subscription Billing</Typography>
        <div className="flex gap-2">
          <Button component={Link} to="/control" size="small" variant="outlined">Tenants</Button>
          <Button component={Link} to="/control/subscriptions" size="small" variant="outlined">Subscriptions</Button>
        </div>
      </div>
      {error && <Typography color="error" className="mb-2">{error}</Typography>}

      <Paper elevation={0} className="p-3 rounded-2xl mb-3" style={{ border: '1px solid var(--mui-palette-divider)' }}>
        <TextField fullWidth size="small" label="Filter by invoice/tenant/status" value={filter} onChange={(e)=>setFilter(e.target.value)} />
      </Paper>

      <Paper elevation={0} className="p-0 overflow-x-auto rounded-2xl" style={{ border: '1px solid var(--mui-palette-divider)' }}>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-3 text-left text-gray-700">Invoice</th>
              <th className="py-2 px-3 text-left text-gray-700">Tenant</th>
              <th className="py-2 px-3 text-left text-gray-700">Amount</th>
              <th className="py-2 px-3 text-left text-gray-700">Status</th>
              <th className="py-2 px-3 text-left text-gray-700">Period</th>
              <th className="py-2 px-3 text-center text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!filtered.length && !loading && (<tr><td colSpan={6} className="text-center text-gray-400 py-6">No bills</td></tr>)}
            {filtered.map(b => (
              <tr key={b._id} className="border-b border-gray-200">
                <td className="py-2 px-3">{b.invoiceNumber}</td>
                <td className="py-2 px-3">{String(b.tenantId)}</td>
                <td className="py-2 px-3">{Number(b.amount).toFixed(2)} {b.currency}</td>
                <td className="py-2 px-3">{b.status}</td>
                <td className="py-2 px-3">{b.periodStart ? new Date(b.periodStart).toLocaleDateString() : '-'} to {b.periodEnd ? new Date(b.periodEnd).toLocaleDateString() : '-'}</td>
                <td className="py-2 px-3 text-center">
                  <Button size="small" variant="outlined" onClick={()=>preview(b._id, false)}>Preview</Button>
                  <Button size="small" variant="outlined" className="ml-2" onClick={()=>preview(b._id, true)}>Download PDF</Button>
                  <Button size="small" variant="outlined" className="ml-2" onClick={()=>sendBill(b._id, 'email')}>Email</Button>
                  <Button size="small" variant="outlined" className="ml-2" onClick={()=>sendBill(b._id, 'whatsapp')}>WhatsApp</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="text-center py-6">Loading...</div>}
      </Paper>
      <Snackbar open={toastOpen} autoHideDuration={3000} onClose={()=>setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={()=>setToastOpen(false)} severity={toastSeverity} variant="filled" sx={{ width: '100%' }}>
          {toastMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}
